import { create } from "zustand";

import { api } from "@/lib/api";
import type {
  ReadChatDto,
  ReadProjectDto,
  ReadProjectFileDto,
  ReadProjectWithRelationsDto
} from "@package/types";
import type { AsyncState } from "./types";

interface ProjectsState extends AsyncState {
  projects: ReadProjectWithRelationsDto[];
}

interface ProjectsActions {
  loadProjects: () => Promise<void>;
  addProject: (project: ReadProjectWithRelationsDto) => void;
  updateProject: (project: ReadProjectWithRelationsDto) => void;
  updateProjectInfo: (project: ReadProjectDto) => void;
  removeProject: (projectId: string) => void;
  addProjectFiles: (projectId: string, files: ReadProjectFileDto[]) => void;
  removeProjectFile: (projectId: string, fileId: number) => void;
  addChatToProject: (projectId: string, chat: ReadChatDto) => void;
  updateProjectChat: (chat: ReadChatDto) => void;
  removeChatFromProjects: (chatId: string) => void;
  reset: () => void;
}

type ProjectsStore = ProjectsState & ProjectsActions;

export const useProjectsStore = create<ProjectsStore>()((set, get) => ({
  projects: [],
  isLoading: false,
  isLoaded: false,
  error: null,

  loadProjects: async () => {
    if (get().isLoaded || get().isLoading) return;

    set({ isLoading: true, error: null });
    try {
      const data = await api.get<ReadProjectWithRelationsDto[]>("/projects");
      set({ projects: data, isLoading: false, isLoaded: true });
    } catch (error) {
      set({ isLoading: false, error: (error as Error).message });
    }
  },

  addProject: (project: ReadProjectWithRelationsDto) =>
    set((state) => ({
      projects: state.projects.some((p) => p.id === project.id)
        ? state.projects
        : [project, ...state.projects]
    })),

  updateProject: (project: ReadProjectWithRelationsDto) =>
    set((state) => ({
      projects: state.projects.map((p) => (p.id === project.id ? project : p))
    })),

  updateProjectInfo: (project: ReadProjectDto) =>
    set((state) => ({
      projects: state.projects.map((existing) =>
        existing.id === project.id
          ? {
              ...existing,
              ...project
            }
          : existing
      )
    })),

  removeProject: (projectId: string) =>
    set((state) => ({
      projects: state.projects.filter((project) => project.id !== projectId)
    })),

  addProjectFiles: (projectId: string, files: ReadProjectFileDto[]) =>
    set((state) => ({
      projects: state.projects.map((project) =>
        project.id === projectId
          ? { ...project, files: [...files, ...project.files] }
          : project
      )
    })),

  removeProjectFile: (projectId: string, fileId: number) =>
    set((state) => ({
      projects: state.projects.map((project) =>
        project.id === projectId
          ? {
              ...project,
              files: project.files.filter((file) => file.id !== fileId)
            }
          : project
      )
    })),

  addChatToProject: (projectId: string, chat: ReadChatDto) =>
    set((state) => ({
      projects: state.projects.map((project) =>
        project.id === projectId
          ? {
              ...project,
              chats: project.chats.some((c) => c.id === chat.id)
                ? project.chats
                : [chat, ...project.chats]
            }
          : project
      )
    })),

  updateProjectChat: (chat: ReadChatDto) =>
    set((state) => ({
      projects: state.projects.map((project) => {
        const withoutChat = project.chats.filter((c) => c.id !== chat.id);

        if (chat.projectId === project.id) {
          return {
            ...project,
            chats: [chat, ...withoutChat]
          };
        }

        return { ...project, chats: withoutChat };
      })
    })),

  removeChatFromProjects: (chatId: string) =>
    set((state) => ({
      projects: state.projects.map((project) => ({
        ...project,
        chats: project.chats.filter((chat) => chat.id !== chatId)
      }))
    })),

  reset: () =>
    set({
      projects: [],
      isLoading: false,
      isLoaded: false,
      error: null
    })
}));
